import {Calendar, Clock, User} from 'lucide-react'
import { Link, useParams } from "react-router-dom"
import Breadcrumbs from "@/components/seo/Breadcrumbs"
import JsonLd from "@/components/seo/JsonLd"
import MedicalDisclaimer from "@/components/seo/MedicalDisclaimer"
import SeoHead, { SITE_NAME, SITE_URL } from "@/components/seo/SeoHead"
import { getBlogPostBySlug, getRelatedArticles } from "@/data/blog"

function BlogPost() {
  const { slug } = useParams()
  const post = slug ? getBlogPostBySlug(slug) : undefined

  if (!post) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-white to-teal-50">
        <SeoHead
          title={`Article Not Found | ${SITE_NAME}`}
          description="This Vitamin Vital Portal article could not be found. Browse the blog for energy, immunity, and Vitamin D guides."
          path="/blog"
          type="website"
        />
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-24 text-center">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Article not found</h1>
          <p className="text-gray-600 mb-8">
            The post you are looking for is unavailable. Return to the Vitamin Vital Portal blog for the latest guides.
          </p>
          <Link
            to="/blog"
            className="inline-flex items-center bg-teal-600 hover:bg-teal-700 text-white px-6 py-3 rounded-full font-semibold transition-colors"
          >
            Back to Blog
          </Link>
        </div>
      </div>
    )
  }

  const relatedArticles = getRelatedArticles(post)
  const path = `/blog/${post.slug}`
  const url = `${SITE_URL}${path}`

  const schema = [
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
        { "@type": "ListItem", position: 2, name: "Blog", item: `${SITE_URL}/blog` },
        { "@type": "ListItem", position: 3, name: post.title, item: url },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: post.title,
      description: post.seoDescription,
      image: post.image,
      author: {
        "@type": "Person",
        name: post.author,
      },
      publisher: {
        "@type": "Organization",
        name: SITE_NAME,
        url: SITE_URL,
      },
      mainEntityOfPage: url,
      articleSection: post.editorialCategory,
      keywords: post.tags.join(", "),
    },
  ]

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-teal-50">
      <SeoHead title={post.seoTitle} description={post.seoDescription} path={path} />
      <JsonLd data={schema} />

      <div className="bg-gradient-to-r from-teal-600 to-teal-700 text-white py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-sm font-medium text-teal-100 mb-4">{post.editorialCategory}</div>
          <h1 className="text-4xl md:text-5xl font-bold mb-6">{post.title}</h1>
          <div className="flex flex-wrap items-center gap-4 text-sm text-teal-100">
            <div className="flex items-center gap-2">
              <User className="w-4 h-4" />
              {post.author}
            </div>
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              {post.date}
            </div>
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4" />
              {post.readTime}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Breadcrumbs
          items={[
            { label: "Home", href: "/" },
            { label: "Blog", href: "/blog" },
            { label: post.title },
          ]}
        />

        <img
          src={post.image}
          alt={post.title}
          className="w-full h-64 md:h-80 object-cover rounded-2xl shadow-lg mb-8"
        />

        <div className="flex flex-wrap gap-2 mb-8">
          {post.categories.map((category) => (
            <span
              key={category}
              className="bg-teal-50 text-teal-700 px-3 py-1 rounded-full text-xs font-semibold"
            >
              {category}
            </span>
          ))}
          {post.tags.map((tag) => (
            <span
              key={tag}
              className="bg-orange-50 text-orange-700 px-3 py-1 rounded-full text-xs font-medium"
            >
              {tag}
            </span>
          ))}
        </div>

        <article className="bg-white rounded-2xl shadow-lg p-8 mb-12">
          <p className="text-lg text-gray-700 leading-relaxed mb-8">{post.intro}</p>
          {post.sections.map((section) => (
            <section key={section.heading} className="mb-8 last:mb-0">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">{section.heading}</h2>
              {section.paragraphs.map((paragraph) => (
                <p key={paragraph} className="text-gray-600 leading-relaxed mb-4 last:mb-0">
                  {paragraph}
                </p>
              ))}
            </section>
          ))}
        </article>

        <section className="mb-12" aria-labelledby="related-articles-heading">
          <h2 id="related-articles-heading" className="text-2xl font-bold text-gray-900 mb-6">
            Related Articles
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {relatedArticles.map((article) => (
              <Link
                key={article.slug}
                to={`/blog/${article.slug}`}
                className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow"
              >
                <img
                  src={article.image}
                  alt={article.title}
                  className="w-full h-36 object-cover"
                />
                <div className="p-5">
                  <div className="text-xs font-semibold text-teal-700 mb-2">
                    {article.categories[0] || article.editorialCategory}
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2 line-clamp-2">
                    {article.title}
                  </h3>
                  <p className="text-sm text-gray-600 line-clamp-3">{article.excerpt}</p>
                </div>
              </Link>
            ))}
          </div>
        </section>

        <div className="flex flex-col sm:flex-row gap-4 mb-12">
          <Link
            to="/blog"
            className="inline-flex items-center justify-center px-8 py-3 bg-white border-2 border-teal-600 text-teal-700 rounded-full font-semibold hover:bg-teal-50 transition-colors"
          >
            More Vitamin Vital Portal Articles
          </Link>
          <Link
            to="/calculator"
            className="inline-flex items-center justify-center px-8 py-3 bg-orange-500 hover:bg-orange-600 text-white rounded-full font-semibold transition-colors"
          >
            Take the Vitality Quiz
          </Link>
        </div>

        <MedicalDisclaimer extra="Vitamin Vital Portal articles are educational and may include affiliate links." />
      </div>
    </div>
  )
}

export default BlogPost
